import { useState, useCallback, useEffect } from 'react';
import { Modal } from 'react-bootstrap-v5';
import moment from 'moment';
import Swal from 'sweetalert2';
import FolhaPagamentoService, { ConciliadoFolhaItem, SugestaoFolhaItem } from '../../services/FolhaPagamentoService';

import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

interface Props {
    show: boolean;
    onHide: () => void;
    cartaoId: number;
}

const PER_PAGE = 20;

const ModalConciliadoFolha = ({ show, onHide, cartaoId }: Props) => {
    const { t } = useTranslation();
    const [aba, setAba] = useState<'conciliados' | 'sugestoes'>('conciliados');
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<ConciliadoFolhaItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [filtro, setFiltro] = useState('');
    const [filtroInput, setFiltroInput] = useState('');

    const [sugestoes, setSugestoes] = useState<SugestaoFolhaItem[]>([]);
    const [loadingSugestoes, setLoadingSugestoes] = useState(false);
    const [processandoSugestoes, setProcessandoSugestoes] = useState(false);

    const load = useCallback(async (pg: number, f: string) => {
        if (!cartaoId) return;
        setLoading(true);
        try {
            const resp = await FolhaPagamentoService.getConciliados(cartaoId, pg, PER_PAGE, f || undefined);
            setItems(resp.items);
            setTotal(resp.total);
        } finally {
            setLoading(false);
        }
    }, [cartaoId]);

    const loadSugestoes = useCallback(async () => {
        if (!cartaoId) return;
        setLoadingSugestoes(true);
        try {
            const resp = await FolhaPagamentoService.getSugestoesPendentes(cartaoId);
            setSugestoes(resp);
        } finally {
            setLoadingSugestoes(false);
        }
    }, [cartaoId]);

    useEffect(() => {
        if (show) {
            setAba('conciliados');
            setPage(1);
            setFiltro('');
            setFiltroInput('');
            load(1, '');
            loadSugestoes();
        }
    }, [show, load, loadSugestoes]);

    const onAceitarTodasSugestoes = useCallback(() => {
        Swal.fire({
            title: t('Aceitar todas as sugestões de conciliação por IA?'),
            showDenyButton: true,
            showConfirmButton: true,
            confirmButtonColor: 'green',
            cancelButtonColor: 'red',
            confirmButtonText: t('Sim'),
            denyButtonText: t('Não'),
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            setProcessandoSugestoes(true);
            try {
                await FolhaPagamentoService.aceitarTodasSugestoes(cartaoId);
                Swal.fire({ text: t('Estamos processando o aceite das sugestões.'), icon: 'success', timer: 3000, showConfirmButton: false });
                onHide();
            } finally {
                setProcessandoSugestoes(false);
            }
        });
    }, [cartaoId, onHide, t]);

    const onRecusarTodasSugestoes = useCallback(() => {
        Swal.fire({
            title: t('Recusar todas as sugestões de conciliação por IA?'),
            showDenyButton: true,
            showConfirmButton: true,
            confirmButtonColor: 'green',
            cancelButtonColor: 'red',
            confirmButtonText: t('Sim'),
            denyButtonText: t('Não'),
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            setProcessandoSugestoes(true);
            try {
                await FolhaPagamentoService.recusarTodasSugestoes(cartaoId);
                Swal.fire({ text: t('Estamos processando a recusa das sugestões.'), icon: 'success', timer: 3000, showConfirmButton: false });
                onHide();
            } finally {
                setProcessandoSugestoes(false);
            }
        });
    }, [cartaoId, onHide, t]);

    const onFiltrar = useCallback(() => {
        setFiltro(filtroInput);
        setPage(1);
        load(1, filtroInput);
    }, [filtroInput, load]);

    const onPageChange = useCallback((newPage: number) => {
        setPage(newPage);
        load(newPage, filtro);
    }, [filtro, load]);

    const totalPages = Math.ceil(total / PER_PAGE);

    const formatVal = (v: number | null) =>
        v == null ? '-' : v.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });

    const formatDate = (d: string | null) =>
        d ? moment(d).format('DD/MM/YYYY') : '-';

    return (
        <Modal size='xl' centered scrollable show={show} onHide={onHide}>
            <div className="modal-content" style={{ backgroundColor: '#F4F5F7' }}>
                <div className="modal-header border-0 pb-1">
                    <div>
                        <h5 className="modal-title fw-bold mb-0" style={{ textTransform: 'uppercase', color: '#5E6397' }}>
                            {t('Conciliação - Folha de Pagamento')}
                        </h5>
                        {aba === 'conciliados' && !loading && (
                            <small className="text-muted">{total} {t('registros conciliados')}</small>
                        )}
                        {aba === 'sugestoes' && !loadingSugestoes && (
                            <small className="text-muted">{sugestoes.length} {t('sugestões pendentes')}</small>
                        )}
                    </div>
                    <button className="btn btn-icon btn-sm btn-active-light-primary ms-2" onClick={onHide}>
                        <i className="fas fa-times" style={{ color: '#5E6397' }} />
                    </button>
                </div>

                <div className="px-4 pt-2 d-flex gap-2" style={{ backgroundColor: '#fff' }}>
                    <button className={`btn btn-sm ${aba === 'conciliados' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setAba('conciliados')}>
                        {t('Conciliados')}
                    </button>
                    <button className={`btn btn-sm ${aba === 'sugestoes' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setAba('sugestoes')}>
                        {t('Sugestões pendentes (IA)')} {sugestoes.length > 0 && <span className="badge bg-warning text-dark ms-1">{sugestoes.length}</span>}
                    </button>
                </div>

                {aba === 'conciliados' && (
                    <div className="px-4 py-3 border-bottom d-flex flex-wrap gap-2 align-items-center" style={{ backgroundColor: '#fff' }}>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            style={{ maxWidth: 300 }}
                            value={filtroInput}
                            onChange={e => setFiltroInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && onFiltrar()}
                            placeholder={t('Filtrar por descrição da rubrica...')}
                        />
                        <button onClick={onFiltrar} disabled={loading} className="btn btn-primary btn-sm">
                            <i className="fas fa-search me-1" />{t('Filtrar')}
                        </button>
                    </div>
                )}

                {aba === 'sugestoes' && sugestoes.length > 0 && (
                    <div className="px-4 py-3 border-bottom d-flex gap-2" style={{ backgroundColor: '#fff' }}>
                        <button disabled={processandoSugestoes} onClick={onAceitarTodasSugestoes} className="btn btn-success btn-sm">
                            <i className="fas fa-check-circle me-1" />{t('Aceitar Todas')}
                        </button>
                        <button disabled={processandoSugestoes} onClick={onRecusarTodasSugestoes} className="btn btn-danger btn-sm">
                            <i className="fas fa-times-circle me-1" />{t('Recusar Todas')}
                        </button>
                    </div>
                )}

                <div className="modal-body p-3" style={{ display: aba === 'sugestoes' ? 'none' : 'block' }}>
                    {loading && (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <span className="spinner-border spinner-border-sm me-2 text-primary" />
                            <span className="text-muted">{t('Carregando...')}</span>
                        </div>
                    )}

                    {!loading && items.length === 0 && (
                        <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                            <i className="fas fa-check-circle fa-3x mb-3" style={{ color: '#ccc' }} />
                            <p className="mb-0">{t('Nenhum registro conciliado encontrado')}</p>
                        </div>
                    )}

                    {!loading && items.length > 0 && (
                        <>
                            <div className="table-responsive">
                                <table className="table table-bordered table-hover table-sm align-middle mb-0">
                                    <thead>
                                        <tr style={{ backgroundColor: '#e8f4fd' }}>
                                            <th colSpan={4} className="text-center fw-bold" style={{ color: '#1565c0', borderRight: '2px solid #90caf9' }}>
                                                <i className="fas fa-file-invoice-dollar me-1" />{t('Rubrica da Folha')}
                                            </th>
                                            <th colSpan={4} className="text-center fw-bold" style={{ color: '#2e7d32' }}>
                                                <i className="fas fa-link me-1" />{t('Vínculo')}
                                            </th>
                                        </tr>
                                        <tr style={{ backgroundColor: '#f8f9fa', fontSize: '0.78rem', textTransform: 'uppercase', color: '#6c757d' }}>
                                            <th className="ps-2">{t('Código')}</th>
                                            <th>{t('Descrição')}</th>
                                            <th className="text-center">{t('Categoria')}</th>
                                            <th className="text-end pe-2" style={{ borderRight: '2px solid #90caf9' }}>{t('Valor')}</th>
                                            <th className="text-center">{t('Tipo')}</th>
                                            <th className="ps-2">{t('Data')}</th>
                                            <th>{t('Histórico')}</th>
                                            <th className="text-end pe-2">{t('Valor')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map(item => (
                                            <tr key={item.rubricaId}>
                                                <td className="ps-2 text-nowrap" style={{ fontSize: '0.85rem' }}>
                                                    {item.codigoRubrica}
                                                </td>
                                                <td style={{ maxWidth: 220, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.descricaoRubrica ?? ''}>
                                                    {item.descricaoRubrica ?? '-'}
                                                </td>
                                                <td className="text-center">
                                                    <span className="badge bg-secondary" style={{ fontSize: '0.68rem' }}>
                                                        {item.categoria ?? '-'}
                                                    </span>
                                                </td>
                                                <td className="text-end pe-2 fw-semibold text-nowrap" style={{ fontSize: '0.85rem', borderRight: '2px solid #90caf9' }}>
                                                    {formatVal(item.valorRubrica)}
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge ${item.tipoVinculo === 'BANCO' ? 'bg-primary' : 'bg-success'}`} style={{ fontSize: '0.72rem' }}>
                                                        {item.tipoVinculo === 'BANCO' ? t('Banco') : t('Razão')}
                                                    </span>
                                                </td>
                                                <td className="ps-2 text-nowrap" style={{ fontSize: '0.85rem' }}>
                                                    {formatDate(item.dataVinculado)}
                                                </td>
                                                <td style={{ maxWidth: 220, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.historicoVinculado ?? ''}>
                                                    {item.historicoVinculado ?? <span className="text-muted fst-italic">{t('Vínculo por soma (sem par único)')}</span>}
                                                </td>
                                                <td className="text-end pe-2 fw-semibold text-nowrap" style={{ fontSize: '0.85rem' }}>
                                                    {formatVal(item.valorVinculado)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <small className="text-muted">
                                        {t('Página')} {page} {t('de')} {totalPages} &middot; {total} {t('registros')}
                                    </small>
                                    <div className="d-flex gap-1">
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            disabled={page <= 1}
                                            onClick={() => onPageChange(page - 1)}
                                        >
                                            <i className="fas fa-chevron-left" />
                                        </button>
                                        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                            const start = Math.max(1, page - 2);
                                            const pg = start + i;
                                            if (pg > totalPages) return null;
                                            return (
                                                <button
                                                    key={pg}
                                                    className={`btn btn-sm ${pg === page ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                    onClick={() => onPageChange(pg)}
                                                >
                                                    {pg}
                                                </button>
                                            );
                                        })}
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            disabled={page >= totalPages}
                                            onClick={() => onPageChange(page + 1)}
                                        >
                                            <i className="fas fa-chevron-right" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {aba === 'sugestoes' && (
                    <div className="modal-body p-3">
                        {loadingSugestoes && (
                            <div className="d-flex justify-content-center align-items-center py-5">
                                <span className="spinner-border spinner-border-sm me-2 text-primary" />
                                <span className="text-muted">{t('Carregando...')}</span>
                            </div>
                        )}

                        {!loadingSugestoes && sugestoes.length === 0 && (
                            <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                                <i className="fas fa-robot fa-3x mb-3" style={{ color: '#ccc' }} />
                                <p className="mb-0">{t('Nenhuma sugestão de conciliação por IA pendente')}</p>
                            </div>
                        )}

                        {!loadingSugestoes && sugestoes.length > 0 && (
                            <div className="table-responsive">
                                <table className="table table-bordered table-hover table-sm align-middle mb-0">
                                    <thead>
                                        <tr style={{ backgroundColor: '#fff8e1', fontSize: '0.78rem', textTransform: 'uppercase', color: '#6c757d' }}>
                                            <th className="ps-2">{t('Código')}</th>
                                            <th>{t('Descrição')}</th>
                                            <th className="text-center">{t('Categoria')}</th>
                                            <th className="text-end pe-2">{t('Valor')}</th>
                                            <th>{t('Justificativa da IA')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sugestoes.map(item => (
                                            <tr key={item.rubricaId}>
                                                <td className="ps-2 text-nowrap" style={{ fontSize: '0.85rem' }}>{item.codigoRubrica}</td>
                                                <td style={{ maxWidth: 200, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.descricaoRubrica ?? ''}>
                                                    {item.descricaoRubrica ?? '-'}
                                                </td>
                                                <td className="text-center">
                                                    <span className="badge bg-secondary" style={{ fontSize: '0.68rem' }}>{item.categoria ?? '-'}</span>
                                                </td>
                                                <td className="text-end pe-2 fw-semibold text-nowrap" style={{ fontSize: '0.85rem' }}>{formatVal(item.valorRubrica)}</td>
                                                <td style={{ fontSize: '0.82rem', fontStyle: 'italic', color: '#6c757d' }}>{item.justificativaMatch ?? '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ModalConciliadoFolha;
